import { useState, useEffect, useCallback, useMemo } from 'react'
import { adminApi, groupsApi } from '../services/api'
import { useAuth } from '../context/AuthContext'
import { useToast } from '../context/ToastContext'
import {
  Shield, Lock, Loader2, X, Check, Pencil, UsersRound,
} from 'lucide-react'
import UserAvatar from './UserAvatar'
import SectionizedUserTable from './SectionizedUserTable'

/**
 * Template Access Control - inline panel or button + modal.
 * Templates are ALWAYS restricted. Only selected users, groups, and admins have access.
 * Admins always have implicit access (no exceptions).
 */
export default function TemplateAccess({ templateId, templateName, inline = false, readOnly = false, managerId = null, approverId = null }) {
  const { user } = useAuth()
  const toast = useToast()
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [accessList, setAccessList] = useState([])
  const [orgUsers, setOrgUsers] = useState([])
  const [selectedUserIds, setSelectedUserIds] = useState(new Set())
  const [savedUserIds, setSavedUserIds] = useState(new Set())
  const [editing, setEditing] = useState(false)
  const [orgGroups, setOrgGroups] = useState([])
  const [selectedGroupIds, setSelectedGroupIds] = useState(new Set())
  const [savedGroupIds, setSavedGroupIds] = useState(new Set())
  const isAdmin = user?.role === 'admin'

  const loadAccess = useCallback(async () => {
    if (!isAdmin && !readOnly) return
    try {
      setLoading(true)
      const [accessRes, usersRes] = await Promise.all([
        adminApi.getTemplateAccess(templateId),
        adminApi.listUsers(),
      ])
      const data = accessRes.data
      setAccessList(data.access || [])
      const ids = new Set((data.access || []).map(a => a.user_id))
      setSelectedUserIds(ids)
      setSavedUserIds(ids)
      setOrgUsers(usersRes.data.users || [])

      // Groups fetch
      try {
        const [groupsRes, groupAccessRes] = await Promise.all([
          groupsApi.list(),
          groupsApi.getTemplateGroupAccess(templateId),
        ])
        setOrgGroups(groupsRes.data || [])
        const gIds = new Set((groupAccessRes.data || []).map(g => g.group_id))
        setSelectedGroupIds(gIds)
        setSavedGroupIds(gIds)
      } catch {
        setOrgGroups([])
        setSelectedGroupIds(new Set())
        setSavedGroupIds(new Set())
      }

      setEditing(false)
    } catch (err) {
      if (err.response?.status !== 403) {
        toast.error('Failed to load access settings')
      }
    } finally {
      setLoading(false)
    }
  }, [templateId, isAdmin, readOnly])

  useEffect(() => { if (open || inline) loadAccess() }, [open, inline, loadAccess])

  const isDirty = useMemo(() => {
    if (savedUserIds.size !== selectedUserIds.size) return true
    for (const id of selectedUserIds) {
      if (!savedUserIds.has(id)) return true
    }
    if (savedGroupIds.size !== selectedGroupIds.size) return true
    for (const id of selectedGroupIds) {
      if (!savedGroupIds.has(id)) return true
    }
    return false
  }, [selectedUserIds, savedUserIds, selectedGroupIds, savedGroupIds])

  const handleToggleUser = (userId) => {
    setSelectedUserIds(prev => {
      const next = new Set(prev)
      if (next.has(userId)) next.delete(userId)
      else next.add(userId)
      return next
    })
  }

  const handleToggleGroup = (groupId) => {
    setSelectedGroupIds(prev => {
      const next = new Set(prev)
      if (next.has(groupId)) next.delete(groupId)
      else next.add(groupId)
      return next
    })
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      await Promise.all([
        adminApi.setTemplateAccess(templateId, {
          is_restricted: true,
          user_ids: Array.from(selectedUserIds)
        }),
        groupsApi.setTemplateGroupAccess(templateId, Array.from(selectedGroupIds)),
      ])
      toast.success(`Access updated \u2014 ${selectedUserIds.size} user(s) + ${selectedGroupIds.size} group(s)`)
      setSavedUserIds(new Set(selectedUserIds))
      setSavedGroupIds(new Set(selectedGroupIds))
      setEditing(false)
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save access')
    } finally {
      setSaving(false)
    }
  }

  const handleCancelEdit = () => {
    setSelectedUserIds(new Set(savedUserIds))
    setSelectedGroupIds(new Set(savedGroupIds))
    setEditing(false)
  }

  if (!isAdmin && !readOnly) return null

  const admins = orgUsers.filter(u => u.role === 'admin' && u.is_active)
  const readers = orgUsers.filter(u => u.role === 'reader' && u.is_active)
  const nonAdminUsers = orgUsers.filter(u => u.role !== 'admin' && u.role !== 'reader' && u.is_active)
  // Plain computation, not useMemo: this runs after the early return above
  // (a hook there breaks the rules of hooks), and its inputs are rebuilt on
  // every render anyway, so a memo would never hit.
  const displayUsers = editing
    ? [...admins, ...readers, ...nonAdminUsers]
    : [...admins, ...readers, ...nonAdminUsers.filter(u => selectedUserIds.has(u.id))]

  // ─── Shared access content ───
  const accessContent = (
    <>
      {/* Access mode info */}
      {!inline && (
        <div className="flex items-center gap-3 p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <Lock className="h-4 w-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Restricted Access</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Admins and readers always have access. Other users need explicit access.</p>
          </div>
        </div>
      )}

      {/* User list */}
      {loading ? (
        <div className="space-y-3 animate-pulse">
          {[1,2,3,4].map(i => <div key={i} className="h-14 bg-gray-100 dark:bg-gray-800 rounded-lg" />)}
        </div>
      ) : (
        <div>
          <SectionizedUserTable
            users={displayUsers}
            sortable
            collapsible={false}
            sectionCounts
            columns={[
              {
                key: 'username',
                label: 'User',
                render: (u) => (
                  <div className="flex items-center gap-3">
                    <UserAvatar user={u} />
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                        {u.username}
                      </div>
                    </div>
                  </div>
                ),
              },
              {
                key: 'email',
                label: 'Email',
                render: (u) => <span className="text-sm text-gray-500 dark:text-gray-400 truncate">{u.email}</span>,
              },
              {
                key: 'job_title',
                label: 'Job Title',
                render: (u) => <span className="text-sm text-gray-500 dark:text-gray-400 truncate">{u.job_title || ''}</span>,
              },
              {
                key: 'role',
                label: 'Role',
                render: (u) => {
                  if (u.role === 'admin') {
                    return (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-navy-100 dark:bg-navy-900/30 text-navy-700 dark:text-navy-300">
                        Admin
                      </span>
                    )
                  }
                  if (u.id === managerId) {
                    return (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300">
                        Template Manager
                      </span>
                    )
                  }
                  if (u.id === approverId) {
                    return (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                        Approver
                      </span>
                    )
                  }
                  if (u.role === 'reader') {
                    return (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                        Reader
                      </span>
                    )
                  }
                  return (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-navy-50 dark:bg-navy-900/20 text-navy-700 dark:text-navy-300">
                      {u.role === 'viewer' ? 'Viewer' : 'User'}
                    </span>
                  )
                },
              },
            ]}
            getSectionKey={(u) => {
              const isDeactivated = u.is_active === false || u.is_active === 0
              if (isDeactivated) return 'deactivated'
              if (u.role === 'admin') return 'admin'
              if (u.id === managerId) return 'template_manager'
              if (u.id === approverId) return 'reviewer'
              if (u.role === 'reader') return 'reader'
              return 'user'
            }}
            renderCheckbox={editing ? (u) => {
              if (u.role === 'admin' || u.role === 'reader') return null
              const hasAccess = selectedUserIds.has(u.id)
              return (
                <div className={`w-[18px] h-[18px] rounded border-2 flex items-center justify-center transition-colors ${
                  hasAccess
                    ? 'bg-navy-600 border-navy-600 dark:bg-navy-500 dark:border-navy-500'
                    : 'border-gray-300 dark:border-gray-600'
                }`}>
                  {hasAccess && <Check className="w-3 h-3 text-white" />}
                </div>
              )
            } : undefined}
            onRowClick={editing ? (u) => {
              if (u.role !== 'admin' && u.role !== 'reader') handleToggleUser(u.id)
            } : undefined}
            rowClassName={editing ? (u) => {
              if (u.role === 'admin') return ''
              return selectedUserIds.has(u.id) ? 'bg-navy-50/50 dark:bg-navy-900/10' : ''
            } : undefined}
          />

          {/* Show count of users without access when not editing */}
          {!editing && nonAdminUsers.filter(u => !selectedUserIds.has(u.id)).length > 0 && (
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
              + {nonAdminUsers.filter(u => !selectedUserIds.has(u.id)).length} user(s) without access
            </p>
          )}

          {/* Groups section */}
          {(() => {
            const displayGroups = editing ? orgGroups : orgGroups.filter(g => selectedGroupIds.has(g.id))
            if (!editing && displayGroups.length === 0 && orgGroups.length === 0) return null
            return (
            <div className="mt-5 pt-4 border-t border-gray-200 dark:border-gray-800">
              <div className="flex items-center gap-2 mb-3">
                <UsersRound className="w-4 h-4 text-navy-600 dark:text-navy-400" />
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Group Access</h3>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                Members of selected groups will automatically have access to this template.
              </p>
              {(editing ? orgGroups : displayGroups).length === 0 ? (
                <p className="text-xs text-gray-400 dark:text-gray-500 italic">No groups assigned</p>
              ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {(editing ? orgGroups : displayGroups).map(group => {
                  const isSelected = selectedGroupIds.has(group.id)
                  const preview = group.members_preview || []
                  const extraCount = Math.max(0, (group.member_count || 0) - preview.length)
                  return (
                    <div
                      key={group.id}
                      onClick={editing ? () => handleToggleGroup(group.id) : undefined}
                      className={`flex items-center gap-3 p-3 rounded-lg border transition-colors min-h-[56px] ${
                        editing ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50' : ''
                      } ${
                        isSelected
                          ? 'border-navy-200 dark:border-navy-800 bg-navy-50/50 dark:bg-navy-900/10'
                          : 'border-gray-200 dark:border-gray-700'
                      }`}
                    >
                      {editing && (
                        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                          isSelected
                            ? 'bg-navy-600 border-navy-600 dark:bg-navy-500 dark:border-navy-500'
                            : 'border-gray-300 dark:border-gray-600'
                        }`}>
                          {isSelected && <Check className="w-3 h-3 text-white" />}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{group.name}</span>
                          {isSelected && !editing && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-medium bg-navy-100 dark:bg-navy-900/30 text-navy-700 dark:text-navy-300 flex-shrink-0">
                              Access
                            </span>
                          )}
                        </div>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                          {group.member_count || 0} member{(group.member_count || 0) !== 1 ? 's' : ''}
                          {group.description && <> &middot; {group.description}</>}
                        </p>
                      </div>
                      {preview.length > 0 && (
                        <div className="flex items-center flex-shrink-0 -space-x-2">
                          {preview.slice(0, 4).map((m) => (
                            <UserAvatar key={m.id} user={m} size="sm" className="ring-2 ring-white dark:ring-gray-900" />
                          ))}
                          {extraCount > 0 && (
                            <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center ring-2 ring-white dark:ring-gray-900 flex-shrink-0">
                              <span className="text-[10px] font-semibold text-gray-600 dark:text-gray-300">+{extraCount}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
              )}
            </div>
            )
          })()}
        </div>
      )}

      {/* Action buttons */}
      {editing && !loading && (
        <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-800">
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {selectedUserIds.size} user(s) + {selectedGroupIds.size} group(s) + {admins.length} admin(s)
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={handleCancelEdit}
              className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !isDirty}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-navy-800 hover:bg-navy-900 text-white text-sm font-medium rounded-lg disabled:opacity-50 transition-colors"
            >
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Save Changes
            </button>
          </div>
        </div>
      )}
    </>
  )

  // ─── Inline mode ───
  if (inline) {
    return (
      <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 mt-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-start gap-2">
            <Shield className="h-4 w-4 text-navy-600 dark:text-navy-400 mt-0.5" />
            <div>
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Access Control</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Admins and readers always have access. Other users need explicit access.
              </p>
            </div>
          </div>
          {!editing && (
            <button
              onClick={() => setEditing(true)}
              disabled={readOnly}
              title={readOnly ? 'Read-only access' : undefined}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-navy-700 dark:text-navy-400 bg-navy-50 dark:bg-navy-900/20 hover:bg-navy-100 dark:hover:bg-navy-900/30 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Pencil className="w-3 h-3" />
              Edit Access
            </button>
          )}
        </div>
        {accessContent}
      </div>
    )
  }

  // ─── Modal mode ───
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg border border-gray-300 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
      >
        <Shield className="h-4 w-4" />
        Access Control
        <span className="ml-1 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-navy-100 dark:bg-navy-900/30 text-navy-700 dark:text-navy-300">
          <Lock className="w-2.5 h-2.5" /> {selectedUserIds.size + admins.length}
        </span>
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <div className="relative w-full max-w-2xl bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-2xl flex flex-col max-h-[85vh]">
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200 dark:border-gray-800">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Access Control</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{templateName}</p>
              </div>
              <button onClick={() => setOpen(false)} className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {accessContent}
            </div>
          </div>
        </div>
      )}
    </>
  )
}
